import { Table } from '@components/display/tables';
import { ActivationOverviewContext } from '@extendedWarranty/contexts/ActivationOverviewContext';
import { useExtendedWarrantyContext } from '@extendedWarranty/contexts/ExtendedWarrantyContext';
import { EditInstallationDialog } from '@extendedWarranty/routes/dashboard/ActivationOverview';
import { InstallationData, ISerialSubmission } from '@extendedWarranty/types/extendedWarranty';
import { areDuplicatesFixed } from '@extendedWarranty/utils/extendedWarranty';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import { Button, Chip, TableCell, ThemeProvider, Typography } from '@mui/material';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { robotoTheme } from 'src/theme';

type UploadedInstallationOverviewProps = {
    name: string;
    data: InstallationData;
};

const tableColumns = [{ id: 'sn', label: 'Serial number' }];

const successTableColumns = [...tableColumns];

const errorEditTableColumns = [...tableColumns, { id: 'actions', label: 'Actions' }];

const UploadedInstallationOverview = ({ name, data }: UploadedInstallationOverviewProps) => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const { installations } = useExtendedWarrantyContext();
    const { handleSerialNumberRemove, handleSerialNumberUndo, setIsPEEnabled } = useContext(ActivationOverviewContext);
    const parentRef = useRef<HTMLDivElement>(null);

    const serialNumbers = useMemo(
        () => ({
            success: data.serial_numbers.filter(
                (sn) => !sn.ValidationMessageError && (!sn.isDuplicate || areDuplicatesFixed(installations, sn))
            ),
            error: data.serial_numbers.filter((sn) => !!sn.ValidationMessageError || sn.isDuplicate),
            edit: data.serial_numbers.filter(
                (sn) => (!!sn.ValidationMessageError || sn.isDuplicate) && !!sn.edit_value
            ),
        }),
        [data.serial_numbers, installations]
    );

    // Filter out, from the error table, duplicate serial numbers that have been corrected
    const errorSNs = useMemo(
        () =>
            serialNumbers.error
                .map((sn) => {
                    if (sn.isDuplicate && !sn.ValidationMessageError && areDuplicatesFixed(installations, sn))
                        return null;
                    return sn;
                })
                .filter((x) => x !== null),
        [installations, serialNumbers.error]
    );

    const renderRow = useCallback(
        (serialNumberData: Partial<ISerialSubmission>, type: 'success' | 'error' | 'edit') => {
            const isInEdit = data.serial_numbers.find((sn) => !!sn.edit_value && sn.id === serialNumberData.id);

            return (
                <>
                    <TableCell
                        component="th"
                        scope="row"
                        className={clsx({
                            '!text-jasper': type === 'error',
                            'line-through': type === 'error' && isInEdit,
                            '!text-blue-cola': type === 'edit',
                        })}
                    >
                        <Typography variant="body2">
                            {type === 'error'
                                ? serialNumberData.SerialNumber
                                : serialNumberData.edit_value || serialNumberData.SerialNumber}
                        </Typography>
                        {type === 'error' ? (
                            <Typography variant="body2">
                                {serialNumberData.ValidationMessageError ||
                                    (serialNumberData.isDuplicate ? 'This serial number is already assigned' : '')}
                            </Typography>
                        ) : null}
                    </TableCell>
                    {type !== 'success' && (
                        <TableCell
                            component="th"
                            scope="row"
                            className={clsx({
                                '!text-jasper': (type === 'error' && !isInEdit) || type === 'edit',
                            })}
                        >
                            <Button
                                variant="text"
                                color="inherit"
                                startIcon={isInEdit && type === 'error' ? <UndoOutlinedIcon /> : <DeleteOutlinedIcon />}
                                onClick={
                                    isInEdit
                                        ? () => handleSerialNumberUndo(name, serialNumberData.id as string)
                                        : () => handleSerialNumberRemove(name, serialNumberData.id as string)
                                }
                            >
                                <Typography variant="body2">
                                    {isInEdit && type === 'error' ? 'Undo Serial Number' : 'Delete Serial Number'}
                                </Typography>
                            </Button>
                        </TableCell>
                    )}
                </>
            );
        },
        [data.serial_numbers, handleSerialNumberRemove, handleSerialNumberUndo, name]
    );

    const handleOpen = () => {
        parentRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
        setIsEditModalOpen(true);
        setIsPEEnabled(true);
    };

    const handleClose = () => {
        setIsEditModalOpen(false);
        setIsPEEnabled(false);
    };

    return (
        <ThemeProvider theme={robotoTheme}>
            <div className="flex flex-col md:flex-row md:items-center mb-5 md:mb-10 gap-5">
                <div className="flex flex-col gap-5">
                    <div>
                        <Typography variant="h5" fontWeight={700}>
                            {name}
                        </Typography>
                        <Typography variant="body1" fontWeight={700}>
                            Installation Date
                        </Typography>
                        <Typography variant="body2" fontWeight={400}>
                            {format(data.date_installed, 'yyyy-MM-dd')}
                        </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                        <Typography variant="body1" fontWeight={700}>
                            Support Toggle:
                        </Typography>
                        <Chip
                            className="text-white font-semibold"
                            color={data.support ? 'confirm-300' : 'error-300'}
                            label={data.support ? 'Active' : 'Inactive'}
                        />
                    </div>
                </div>
                {serialNumbers.error.length ? (
                    <div className="md:ml-auto">
                        <Button
                            color="victron-blue"
                            startIcon={<EditIcon />}
                            disableElevation={false}
                            className="!rounded"
                            onClick={handleOpen}
                            fullWidth
                        >
                            EDIT SERIAL NUMBERS
                        </Button>
                    </div>
                ) : null}
            </div>
            {errorSNs.length ? (
                <div className="mb-10 relative" ref={parentRef}>
                    <div className="md:max-w-[366px] flex py-4 px-5 gap-5 bg-jasper rounded-lg mb-3">
                        <ErrorOutlineIcon color="white" />
                        <div className="text-white flex flex-col gap-1">
                            <Typography variant="body1" fontWeight={600}>
                                Incorrect Serial Numbers
                            </Typography>
                            <Typography variant="body2" fontWeight={400}>
                                Serial numbers in this table are not processed. Please update the serial numbers before
                                reuploading.
                            </Typography>
                        </div>
                    </div>
                    <Table
                        containerClassname="bg-white"
                        headers={errorEditTableColumns}
                        onSortClick={() => {}}
                        data={errorSNs}
                        renderRow={(item) => renderRow(item, 'error')}
                        emptyListTxt={'List is empty, sorry'}
                        className="min-w-[500px]"
                        count={errorSNs.length}
                    />
                    {isEditModalOpen ? (
                        <EditInstallationDialog
                            open={true}
                            container={parentRef.current}
                            onClose={handleClose}
                            editables={errorSNs}
                            installationId={name}
                        />
                    ) : null}
                </div>
            ) : null}
            {serialNumbers.edit.length ? (
                <div className="mb-10">
                    <div className="max-w-[366px] flex py-4 px-5 gap-5 bg-alert-info-bg rounded-lg mb-3">
                        <ErrorOutlineIcon color="info" />
                        <div className="text-ateneo-blue flex flex-col gap-1">
                            <Typography variant="body1" fontWeight={600}>
                                Serial Numbers Updated
                            </Typography>
                            <Typography variant="body2" fontWeight={400}>
                                Serial numbers in this table were changed.
                            </Typography>
                        </div>
                    </div>
                    <Table
                        containerClassname="bg-white"
                        headers={errorEditTableColumns}
                        onSortClick={() => {}}
                        data={serialNumbers.edit}
                        renderRow={(item) => renderRow(item, 'edit')}
                        className="min-w-[500px]"
                        count={serialNumbers.edit.length}
                    />
                </div>
            ) : null}
            {serialNumbers.success.length ? (
                <div className="mb-10">
                    <div className="max-w-[366px] flex py-4 px-5 gap-5 bg-caribbean-green rounded-lg mb-3">
                        <CheckCircleOutlinedIcon color="white" />
                        <div className="text-white flex flex-col gap-1">
                            <Typography variant="body1" fontWeight={600}>
                                Uploaded
                            </Typography>
                            <Typography variant="body2" fontWeight={400}>
                                Serial numbers uploaded successfully.
                            </Typography>
                        </div>
                    </div>
                    <Table
                        containerClassname="bg-white"
                        headers={successTableColumns}
                        onSortClick={() => {}}
                        data={serialNumbers.success}
                        renderRow={(item) => renderRow(item, 'success')}
                        emptyListTxt=""
                        className="min-w-[500px]"
                        count={data.serial_numbers.filter((sn) => !sn.ValidationMessageError).length}
                    />
                </div>
            ) : null}
            {!data.serial_numbers.length ? (
                <div className="mb-10">
                    <Table
                        containerClassname="bg-white"
                        headers={successTableColumns}
                        onSortClick={() => {}}
                        data={[]}
                        renderRow={(item) => renderRow(item, 'success')}
                        emptyListTxt="No serial numbers remain for this installation. As a result, this installation will not be saved in Professional."
                        emptyListClassName="min-h-fit p-8"
                        className="min-w-[500px]"
                        count={0}
                    />
                </div>
            ) : null}
        </ThemeProvider>
    );
};

export { UploadedInstallationOverview };
